import React, { useState, useEffect } from "react";
import { PRODUCT_CATEGORIES } from "../constants/categories";
import { hasSubcategories, subcategoriesFor } from "../constants/subcategories";
import { platformAPI } from "../services/api";
import JuiceDetailsFields, {
  DEFAULT_JUICE_DETAILS,
  parseJuiceDetails,
  serializeJuiceDetails,
} from "./JuiceDetailsFields";

const API_ORIGIN = process.env.REACT_APP_API_URL || "http://localhost:3010/api";

const inputClass =
  "w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500";
const labelClass = "block text-sm font-medium text-gray-700 mb-2";

/**
 * The template equivalent of admin-dashboard's ProductForm — same fields and
 * layout, minus everything that's a shop's own business decision rather than
 * part of the product: price, cost, discount and stock. A template is what a
 * new owner is handed to price and stock themselves.
 */
export default function TemplateProductForm({ templateId, item, onClose, onSaved }) {
  const [formData, setFormData] = useState({
    name: "",
    category: "juice",
    brand: "",
    subcategory: "",
    description: "",
    variant_label: "",
  });
  const [juiceDetails, setJuiceDetails] = useState(DEFAULT_JUICE_DETAILS);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [existingImages, setExistingImages] = useState([]);
  const [imagePreviews, setImagePreviews] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (item) {
      setFormData({
        name: item.name || "",
        category: item.category || "juice",
        brand: item.brand || "",
        subcategory: item.subcategory || "",
        description: item.description || "",
        variant_label: item.variant_label || "",
      });

      if (item.juice_details) setJuiceDetails(parseJuiceDetails(item.juice_details));

      if (item.images && Array.isArray(item.images)) {
        setExistingImages(item.images);
      }
    }
  }, [item]);

  const handleImageSelect = (e) => {
    const files = Array.from(e.target.files);
    setSelectedFiles(files);
    setImagePreviews(files.map((file) => URL.createObjectURL(file)));
  };

  const removeExistingImage = (index) => {
    setExistingImages((prev) => prev.filter((_, i) => i !== index));
  };

  const clearNewImages = () => {
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setSelectedFiles([]);
    setImagePreviews([]);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      const body = new FormData();
      body.append("name", formData.name);
      body.append("category", formData.category);
      body.append("brand", formData.brand || "");
      body.append("subcategory", formData.subcategory || "");
      body.append("description", formData.description || "");
      // Only sent for a product that is in a range; omitting it leaves the range untouched.
      if (item?.variant_group_id) body.append("variant_label", formData.variant_label);

      if (formData.category === "juice") {
        body.append("juice_details", JSON.stringify(serializeJuiceDetails(juiceDetails)));
      }

      selectedFiles.forEach((file) => body.append("images", file));

      if (item) {
        body.append("existingImages", JSON.stringify(existingImages));
        await platformAPI.updateTemplateItem(templateId, item.id, body);
      } else {
        await platformAPI.addTemplateItem(templateId, body);
      }

      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || "Failed to save");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div
      style={{ marginTop: 0 }}
      className="fixed inset-0 bg-black bg-opacity-50 flex items-start sm:items-center justify-center z-50 p-0 sm:p-4"
    >
      <div className="bg-white sm:rounded-xl shadow-2xl max-w-4xl w-full max-h-[100vh] sm:max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b px-6 py-4">
          <div className="flex items-center justify-between">
            <h2 className="text-2xl font-bold text-gray-800">
              {item ? "Edit template product" : "Add a new product"}
            </h2>
            <button
              onClick={onClose}
              aria-label="Close"
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ✕
            </button>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <p className="text-red-600">{error}</p>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className={labelClass}>Name</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className={inputClass}
                required
              />
            </div>

            <div>
              <label className={labelClass}>Brand</label>
              <input
                type="text"
                value={formData.brand}
                onChange={(e) => setFormData({ ...formData, brand: e.target.value })}
                className={inputClass}
              />
            </div>

            <div>
              <label className={labelClass}>Category</label>
              <select
                value={formData.category}
                onChange={(e) =>
                  setFormData({ ...formData, category: e.target.value, subcategory: "" })
                }
                className={inputClass}
                required
              >
                {PRODUCT_CATEGORIES.map((category) => (
                  <option key={category.value} value={category.value}>
                    {category.label}
                  </option>
                ))}
              </select>
            </div>

            {hasSubcategories(formData.category) && (
              <div>
                <label className={labelClass}>Subcategory</label>
                <select
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className={inputClass}
                >
                  <option value="">No subcategory</option>
                  {subcategoriesFor(formData.category).map((subcategory) => (
                    <option key={subcategory.value} value={subcategory.value}>
                      {subcategory.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
          </div>

          {item?.variant_group_id && (
            <div>
              <label className={labelClass}>Label in range</label>
              <input
                type="text"
                value={formData.variant_label}
                onChange={(e) => setFormData({ ...formData, variant_label: e.target.value })}
                className={inputClass}
                placeholder="e.g. Watermelon Ice"
                required
              />
            </div>
          )}

          <div>
            <label className={labelClass}>Description</label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              rows="3"
              className={inputClass}
            />
          </div>

          <div className="border-t pt-6">
            <h3 className="text-lg font-semibold text-gray-800 mb-4">Images</h3>

            {existingImages.length > 0 && (
              <div className="mb-4">
                <label className={labelClass}>Current images</label>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {existingImages.map((img, index) => (
                    <div key={index} className="relative group">
                      <img
                        src={`${API_ORIGIN}${img}`}
                        alt=""
                        className="w-full h-32 object-cover rounded-lg border"
                      />
                      <button
                        type="button"
                        onClick={() => removeExistingImage(index)}
                        aria-label="Remove image"
                        className="absolute top-2 end-2 bg-red-500 text-white p-1 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div>
              <label className={labelClass}>{item ? "Add images" : "Upload images"}</label>
              <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleImageSelect}
                className={inputClass}
              />
              <p className="text-xs text-gray-500 mt-1">JPEG, PNG, GIF or WebP, up to 5MB each.</p>
            </div>

            {imagePreviews.length > 0 && (
              <div className="mt-4">
                <div className="flex items-center justify-between mb-2">
                  <label className="block text-sm font-medium text-gray-700">New (preview)</label>
                  <button
                    type="button"
                    onClick={clearNewImages}
                    className="text-sm text-red-600 hover:text-red-800"
                  >
                    Clear all
                  </button>
                </div>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {imagePreviews.map((preview, index) => (
                    <img
                      key={index}
                      src={preview}
                      alt=""
                      className="w-full h-32 object-cover rounded-lg border"
                    />
                  ))}
                </div>
              </div>
            )}
          </div>

          {formData.category === "juice" && (
            <JuiceDetailsFields value={juiceDetails} onChange={setJuiceDetails} />
          )}

          <div className="flex justify-end gap-4 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium transition-all"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={loading}
              className="px-6 py-2 bg-primary-600 hover:bg-primary-700 text-white rounded-lg font-semibold transition-all disabled:opacity-50"
            >
              {loading ? "Saving…" : item ? "Save changes" : "Add product"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
